```vue
<template>
  <div class="container my-4">

    <!-- Breadcrumb -->
    <nav
      aria-label="breadcrumb"
      class="mb-4"
    >
      <ol class="breadcrumb">

        <li class="breadcrumb-item">
          <router-link to="/books">
            Home
          </router-link>
        </li>

        <li
          v-if="book"
          class="breadcrumb-item"
        >
          {{ book.category }}
        </li>

        <li
          v-if="book"
          class="breadcrumb-item active"
          aria-current="page"
        >
          {{ book.name }}
        </li>

      </ol>
    </nav>


    <!-- Book Details -->
    <div
      v-if="book"
      class="row g-5"
    >

      <!-- Book Image -->
      <div class="col-md-5">

        <div class="image-container">

          <img
            :src="book.image"
            :alt="book.name"
            class="book-detail-image"
          >

        </div>

      </div>


      <!-- Book Information -->
      <div class="col-md-7">

        <!-- Category + Rating -->
        <div
          class="d-flex align-items-center gap-2 mb-3"
        >

          <span class="badge bg-primary">
            {{ book.category }}
          </span>

          <span class="text-warning fw-bold">
            ⭐ {{ book.rating || '4.5' }}
          </span>

        </div>


        <!-- Title -->
        <h1 class="book-detail-title mb-3">
          {{ book.name }}
        </h1>


        <!-- Author -->
        <p class="book-detail-info">
          <strong>Author:</strong>
          {{ book.author }}
        </p>


        <!-- ISBN -->
        <p class="book-detail-info">
          <strong>ISBN:</strong>
          {{ book.ISBN }}
        </p>


        <!-- Pages -->
        <p class="book-detail-info">
          <strong>Pages:</strong>
          {{ book.pages }} pages
        </p>


        <!-- Price -->
        <h2 class="book-price my-4">
          {{ formatPrice(book.price) }}
        </h2>


        <!-- Seller Actions -->
        <div
          v-if="isSeller"
          class="seller-actions mt-4 pt-3"
        >

          <router-link
            :to="'/books/edit/' + book.id"
            class="btn btn-warning"
          >
            ✏️ Edit Details
          </router-link>

          <button
            class="btn btn-danger"
            @click="handleDelete"
          >
            🗑️ Delete Book
          </button>

        </div>


        <!-- Buyer Actions -->
        <div
          v-if="isBuyer"
          class="buyer-actions mt-4 pt-3"
        >

          <!-- Wishlist Button -->
          <button
            class="btn"
            :class="
              isInWishlist
                ? 'btn-secondary'
                : 'btn-outline-danger'
            "
            :disabled="isInWishlist"
            @click="addToWishlist"
          >
            {{
              isInWishlist
                ? '✓ In Wishlist'
                : '♡ Add to Wishlist'
            }}
          </button>


          <!-- Cart Button -->
          <button
            class="btn"
            :class="
              isInCart
                ? 'btn-success'
                : 'btn-primary'
            "
            @click="addToCart"
          >
            {{
              isInCart
                ? '🛒 Added to Cart'
                : '🛒 Add to Cart'
            }}
          </button>

        </div>

      </div>

    </div>


    <!-- Loading -->
    <div
      v-else-if="loading"
      class="text-center py-5"
    >

      <div
        class="spinner-border text-primary"
        role="status"
      ></div>

      <p class="mt-3">
        Loading book details...
      </p>

    </div>


    <!-- Book Not Found -->
    <div
      v-else
      class="text-center py-5"
    >

      <h3>
        Book not found
      </h3>

      <router-link
        to="/books"
        class="btn btn-primary mt-3"
      >
        Back to Books
      </router-link>

    </div>

  </div>
</template>


<script>
export default {
  name: 'BookDetails',

  computed: {

    books() {
      return this.$store.state.books
    },

    wishlist() {
      return this.$store.state.wishlist
    },

    loading() {
      return this.$store.state.loading
    },

    userRole() {

      const savedUser =
        localStorage.getItem('currentUser')

      if (!savedUser) {
        return null
      }

      return JSON.parse(savedUser).role
    },

    isBuyer() {
      return this.userRole === 'buyer'
    },

    isSeller() {
      return this.userRole === 'seller'
    },

    book() {

      return this.books.find(
        book =>
          book.ISBN ===
          this.$route.params.ISBN
      )
    },

    isInWishlist() {

      if (!this.book) {
        return false
      }

      return this.wishlist.some(
        item =>
          item.ISBN ===
          this.book.ISBN
      )
    },

    isInCart() {

      if (!this.book) {
        return false
      }

      return this.$store.getters.isInCart(
        this.book.ISBN
      )
    }

  },

  methods: {

    formatPrice(value) {

      return new Intl.NumberFormat(
        'en-SA',
        {
          style: 'currency',
          currency: 'SAR'
        }
      ).format(value)
    },

    addToWishlist() {

      if (!this.book) {
        return
      }

      this.$store.dispatch(
        'addToWishlist',
        this.book
      )
    },

    addToCart() {

      if (!this.book) {
        return
      }

      this.$store.dispatch(
        'addToCart',
        this.book
      )
    },

    async handleDelete() {

      if (!this.book) {
        return
      }

      const confirmed =
        window.confirm(
          `Are you sure you want to delete "${this.book.name}"?`
        )

      if (!confirmed) {
        return
      }

      const result =
        await this.$store.dispatch(
          'deleteBook',
          this.book.id
        )

      if (!result.success) {

        alert(result.message)

        return
      }

      this.$router.push('/books')
    }

  }
}
</script>


<style scoped>

.image-container {
  width: 100%;
  height: 500px;

  display: flex;
  justify-content: center;
  align-items: center;

  background: var(--surface);

  border-radius: 20px;

  padding: 20px;

  box-shadow:
    0 8px 30px
    rgba(108, 59, 255, 0.10);
}


.book-detail-image {
  width: 100%;
  height: 100%;

  object-fit: contain;

  border-radius: 12px;
}


.book-detail-title {
  color: var(--text);
  font-weight: 700;
}


.book-detail-info {
  color: var(--text-secondary);
  font-size: 1.05rem;
  margin-bottom: 12px;
}


.book-detail-info strong {
  color: var(--text);
}


.book-price {
  color: var(--primary);
  font-weight: 700;
}


.seller-actions,
.buyer-actions {
  border-top:
    1px solid var(--border);

  display: flex;

  gap: 10px;

  flex-wrap: wrap;
}


.buyer-actions .btn,
.seller-actions .btn {
  min-width: 170px;

  border-radius: 10px;

  font-weight: 600;
}


.btn {
  border-radius: 10px;
  font-weight: 600;
}


@media (max-width: 768px) {

  .image-container {
    height: 400px;
  }

  .book-detail-title {
    font-size: 1.8rem;
  }

}

</style>
```
